import { useRef, useEffect, useState } from "react";

import "./styles.scss";

import { CommunityBanner } from "@components/Community";

import { useParams, useLocation } from "react-router-dom";

import { useAuth } from "@contexts/AuthContext";
import { useCommunity } from "@contexts/CommunityContext";

import { ReactComponent as FullLogo } from "@assets/logo/M_WHITE_LOGO_FULL.svg";
import { ReactComponent as SmallLogo } from "@assets/logo/M_WHITE_LOGO_SQUARE.svg";

import { ReactComponent as Hamburger } from "@assets/custom-icons/hamburger.svg";

import * as Material from "@mui/material";

import {
    AccountBalanceRounded,
    AddRounded,
    ArrowDropDownRounded,
    InfoRounded,
    InsightsRounded,
} from "@mui/icons-material";

import { Divider } from "@mui/material";

import UserCard from "./UserCard";

import { Text } from "@fragments";


export default function Sidebar() {
    const sidebarRef = useRef(null)
    const location = useLocation();

    // Constantes para o estado da sidebar
    const SIDEBAR_STATE_KEY = 'ginastee-sidebar-state';
    const MOBILE_BREAKPOINT = 1200;

    // Estados locais
    const [isOpen, setIsOpen] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [currentSlug, setCurrentSlug] = useState('');

    const { id } = useParams();

    const { communities, community } = useCommunity();

    const checkedId = getCurrentPageOnSidebar();

    // Função para extrair o slug da URL atual
    const extractSlugFromPath = (pathname) => {
        // Remove a barra inicial se existir
        const cleanPath = pathname.replace(/^\//, '');

        // Se o pathname não está vazio, extrai o primeiro segmento (slug)
        if (cleanPath) {
            // Divide o path pelos '/' e pega o primeiro segmento
            const segments = cleanPath.split('/');
            const slug = segments[0];

            // Verifica se o slug não está vazio e não é uma rota especial
            if (slug && !['community', 'content', 'contenthub', 'dashboard', 'banking'].includes(slug)) {
                return slug;
            }
        }

        return '';
    };

    // Efeito para detectar mudanças na URL e atualizar o slug atual
    useEffect(() => {
        const slug = extractSlugFromPath(location.pathname);
        setCurrentSlug(slug);
    }, [location.pathname]);

    // Função para inicializar o estado da sidebar
    useEffect(() => {
        const initializeSidebarState = () => {

            if (isInitialized) return

            const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

            if (isMobile) {
                // Em telas menores que 1200px, inicia fechada mas permite abertura manual
                setIsOpen(false);
            } else {
                // Em telas maiores que 1200px, recupera do local storage
                const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);

                if (savedState !== null) {
                    // Se há estado salvo, usa ele
                    setIsOpen(JSON.parse(savedState));
                } else {
                    // Se não há estado salvo, inicia aberta
                    setIsOpen(true);
                }
            }

            setIsInitialized(true);
        };

        // Inicializa o estado
        initializeSidebarState();

        // Adiciona listener para mudanças de tamanho da tela
        const handleResize = () => {
            const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

            if (isMobile && isOpen && !isInitialized) {
                // Se mudou para mobile e está aberta, fecha apenas na inicialização
                setIsOpen(false);
            } else if (!isMobile && !isInitialized) {
                // Se mudou para desktop e não foi inicializado, recupera estado
                const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
                if (savedState !== null) {
                    setIsOpen(JSON.parse(savedState));
                } else {
                    setIsOpen(true);
                }
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isOpen, isInitialized]);

    // Efeito para fazer scroll para o top sempre que a rota mudar
    useEffect(() => {
        if (sidebarRef.current) {
            sidebarRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }, [location.pathname]);

    // Função para alternar o estado da sidebar
    const toggleSidebar = (state = null) => {
        const newState = state !== null ? state : !isOpen;
        setIsOpen(newState);
        localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(newState));
    };

    // Atualiza a classe CSS baseada no estado
    useEffect(() => {
        if (sidebarRef.current) {
            if (isOpen) {
                sidebarRef.current.classList.add('open');
            } else {
                sidebarRef.current.classList.remove('open');
            }
        }
    }, [isOpen]);

    // Mantém a função global para compatibilidade
    window.toggleSidebar = toggleSidebar;

    function handleOpenCommunity({ slug }) {
        community.open(slug);
    }
    function getCurrentPageOnSidebar() {
        return communities?.filter((c) => id == c.id)[0]?.id;
    }

    function SidebarItem({ title, left, futureLaunch = false, onClick, variant = "default", isSelected = false, className = "" }) {
        return (
            <button
                disabled={futureLaunch}
                onClick={() => {
                    if (window.innerWidth < MOBILE_BREAKPOINT) {
                        toggleSidebar(false)
                    }
                    onClick()
                }}
                className={
                    "sidebar-item " + (variant) +
                    (isSelected ? " sidebar-item-selected " : "") + " " + className
                }
            >
                {
                    isOpen ? (
                        <Text.h5 left={left} weight={isSelected ? 400 : 300} contrast={isSelected ? 10 : 7} className="list-text">{title}</Text.h5>
                    ) : left
                }
                {
                    futureLaunch && isOpen && (
                        <Text.span weight={500} contrast={9} className="coming-soon">Em breve</Text.span>
                    )
                }
            </button>
        )
    }

    function UserSelect() {
        const { user, handleOpenProfileConfigModal } = useAuth();
        const [anchorEl, setAnchorEl] = useState(null);
        const userSelectRef = useRef(null);

        const handleClick = (event) => {
            setAnchorEl(userSelectRef.current);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        const open = Boolean(anchorEl);

        return (
            <>
                <div
                    ref={userSelectRef}
                    className="user-select mb-3 pressable"
                    onClick={handleClick}
                >
                    <img src={user?.avatar} className="select-avatar" />
                    <div className="user-select-text sidebar-text">
                        <h4>{user?.username}</h4>
                        <h5 className="select-nickname">{user?.nickname ? '@' + user?.nickname : null}</h5>
                    </div>
                    <ArrowDropDownRounded />
                </div>
                <UserCard
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    onOpenProfileConfig={handleOpenProfileConfigModal}
                />
            </>
        );
    }

    function CreateCommunityButton() {
        const [loading, setLoading] = useState(false);


        async function handleCreateCommunity() {
            setLoading(true);
            await community.management.create();
            setLoading(false);
        }

        return (
            <SidebarItem
                title={loading ? 'Criando comunidade...' : 'Crie sua comunidade'}
                left={<>
                    {loading
                        ? <Material.CircularProgress size={20} color="white" />
                        : <AddRounded className="sidebar-icon csf-2" />}
                </>}
                onClick={handleCreateCommunity}
                variant="dashed"
            />
        );
    }

    return (
        <div ref={sidebarRef} className="sidebar-menu pt-2 border-sf-3">
            <div className="header mb-2">
                <Material.IconButton onClick={() => toggleSidebar()} className="hamburger-btn">
                    <Hamburger />
                </Material.IconButton>

                {
                    isOpen ? <FullLogo className="logo" /> : <SmallLogo className="logo" />
                }
            </div>

            <UserSelect />

            <Text.span weight={500} contrast={9} className="sidebar-title mb-1 mt-2 sidebar-text">COMUNIDADES</Text.span>

            <CreateCommunityButton />

            <Divider className="mb-1 mt-1" />

            {communities?.map((c, i) => (
                <SidebarItem
                    key={i}
                    title={c.name}
                    left={<CommunityBanner small={true} color="white" className="sidebar-icon" banner={c.banner} />}
                    onClick={() => handleOpenCommunity(c)}
                    isSelected={currentSlug === c.slug}
                />
            ))}

            <div className="sidebar-bottom mt-4">
                <Text.span weight={500} contrast={9} className="sidebar-title sidebar-text mt-1">ADMINISTRAÇÃO</Text.span>
                <SidebarItem
                    title="Banking"
                    left={<AccountBalanceRounded color="white" className="sidebar-icon" />}
                    onClick={() => window.navigate("/banking/charges")}
                    isSelected={location.pathname.includes("/banking")}
                    className="mt-1"
                />
                <SidebarItem
                    title="Relatórios"
                    left={<InsightsRounded color="white" className="sidebar-icon" />}
                    onClick={() => window.navigate("/dashboard")}
                    isSelected={location.pathname.includes("/dashboard")}
                    futureLaunch={true}
                />
                <SidebarItem
                    title="Suporte"
                    left={<InfoRounded color="white" className="sidebar-icon" />}
                    onClick={() => window.open("https://api.whatsapp.com/send/?phone=5531971583279&text&type=phone_number&app_absent=0")}
                    isSelected={location.pathname.includes("/support")}
                />
            </div>
        </div>
    );
}